// Full-screen Google placement chrome — YouTube Shorts and YouTube
// in-stream. The Google counterpart to overlays.tsx.
//
// Same contract as the Meta overlays: everything is absolutely
// positioned inside the media box, consumes no layout, and has
// pointer-events off so native <video> controls stay clickable
// underneath. Geometry is in `u` = 1% of media width.
//
// These are approximations of the current YouTube mobile/web builds.
// Google publishes asset specs (sizes, file limits) but not placement
// UI geometry, so treat the numbers as a close read of the live product
// rather than a spec — re-calibrate against screenshots if pixel-parity
// ever matters. The same caveat already applies to the Meta overlays.

import type { CSSProperties } from 'react';
import { Icon, Avatar } from './icons';
import { displayDomain } from './overlays';
import type { ChromeDensity, ChromeIdentity } from './types';

// YouTube sets Roboto ahead of the system stack; keeping that order is
// most of why the chrome reads as Google rather than as iOS.
const FONT = "Roboto, -apple-system, 'Segoe UI', Arial, sans-serif";
const LOREM = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor';

const shadowText: CSSProperties = { textShadow: '0 1px 2px rgba(0,0,0,.6)' };

const overlayBase: CSSProperties = {
  position: 'absolute', inset: 0, pointerEvents: 'none', color: '#fff',
  fontFamily: FONT, overflow: 'hidden', zIndex: 5
};

type OverlayProps = {
  identity:        ChromeIdentity;
  u:               number;
  density:         ChromeDensity;
  showSponsored?:  boolean;
  showEngagement?: boolean;
};

// ── YouTube Shorts ───────────────────────────────────────────────────
// Where a PMax vertical video (pmax_video_9_16) actually lands.
//
// Structurally close to Reels — bottom scrim, right action rail,
// bottom-left identity block — but the differences are the point of
// previewing it: Shorts adds a dislike button to the rail (so the rail
// is TALLER than Reels'), the CTA is a full-width filled button under
// the channel row rather than a light pill, and the sponsored label
// reads as a discrete chip rather than a caption line.

export function YouTubeShortsChrome({
  identity, u, density, showSponsored = true, showEngagement = true
}: OverlayProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';

  // Rail order matches Shorts: like, dislike, comment, share, then the
  // advertiser avatar at the bottom. Compact tiles drop the counts and
  // the share/avatar tail — below ~200px they turn to mush.
  const railItems: Array<[string, string]> = compact
    ? [['thumb', ''], ['thumbDown', ''], ['comment', '']]
    : [['thumb', '4.2K'], ['thumbDown', 'Dislike'], ['comment', '128'], ['shareYt', 'Share']];

  return (
    <div style={overlayBase} aria-hidden>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '46%',
        background: 'linear-gradient(transparent, rgba(0,0,0,.66))'
      }} />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: '14%',
        background: 'linear-gradient(rgba(0,0,0,.42), transparent)'
      }} />

      {/* Top row — the Shorts wordmark sits left, search/kebab right. */}
      {!compact && (
        <div style={{
          position: 'absolute', top: u * 3.4, left: u * 4, right: u * 4,
          display: 'flex', alignItems: 'center', gap: u * 3, ...shadowText
        }}>
          <b style={{ fontSize: u * 4, fontWeight: 700, letterSpacing: '-0.02em' }}>Shorts</b>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: u * 3.4 }}>
            <Icon name="search" size={u * 5.4} shadow />
            <Icon name="dots" size={u * 5.4} shadow />
          </span>
        </div>
      )}

      {/* Right action rail. Sits higher than Reels' because Shorts has
          the extra dislike slot and a taller CTA block beneath it. */}
      {showEngagement && (
        <div style={{
          position: 'absolute', right: u * 3.4, bottom: u * (compact ? 17 : 24),
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: u * (compact ? 4.2 : 4.6)
        }}>
          {railItems.map(([name, count]) => (
            <div key={name} style={{ textAlign: 'center' }}>
              <Icon
                name={name === 'thumbDown' ? 'thumb' : name}
                size={u * 7}
                shadow
                // Shorts' dislike is the like glyph rotated — no separate
                // asset, which is also how it reads on screen.
                style={name === 'thumbDown' ? { transform: 'rotate(180deg)' } : undefined}
              />
              {count && (
                <b style={{ display: 'block', fontSize: u * 2.7, fontWeight: 500, marginTop: u * 0.8, ...shadowText }}>
                  {count}
                </b>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Identity + CTA stack. Inset on the right to clear the rail. */}
      <div style={{
        position: 'absolute', left: u * 4, right: u * 13, bottom: u * 4,
        display: 'flex', flexDirection: 'column', gap: u * 2.4
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: u * 2.2, fontSize: u * 3.5, fontWeight: 600, ...shadowText }}>
          <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 8} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{identity.handle}</span>
          {showSponsored && !compact && (
            // YouTube marks paid placements with a chip, not a caption
            // line — the visual weight lands differently from Meta's
            // "Sponsored" text and it's worth seeing that difference.
            <span style={{
              flex: 'none', background: 'rgba(255,255,255,.22)', borderRadius: u * 0.8,
              padding: `${u * 0.7}px ${u * 1.6}px`, fontSize: u * 2.6, fontWeight: 500
            }}>
              Sponsored
            </span>
          )}
        </div>
        {showEngagement && !compact && (
          <div style={{ fontSize: u * 3.1, lineHeight: 1.35, opacity: 0.95, ...shadowText }}>
            {LOREM.slice(0, 62)}&hellip;
          </div>
        )}
        {/* CTA — a full-width filled button, the Shorts ad treatment.
            Deliberately heavier than the Reels pill: it eats more of the
            frame, which is exactly what an operator needs to see. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: u * 1.6,
          background: '#fff', color: '#0f0f0f', borderRadius: u * 4.5,
          padding: `${u * 2.8}px ${u * 3.2}px`, fontSize: u * 3.5, fontWeight: 600
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cta}</span>
          <Icon name="chevR" size={u * 3.6} style={{ stroke: '#0f0f0f' }} />
        </div>
      </div>

      {/* Scrub bar along the very bottom. */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: u * 0.5,
        background: 'rgba(255,255,255,.28)'
      }}>
        <i style={{ display: 'block', width: '38%', height: '100%', background: '#fff' }} />
      </div>
    </div>
  );
}

// ── YouTube in-stream ────────────────────────────────────────────────
// Where a PMax landscape video (pmax_video_16_9) runs as pre/mid-roll.
//
// The elements that persist for the whole ad — and therefore the ones
// worth previewing — are the yellow progress bar, the bottom-left
// advertiser card, and the Skip button. The transport controls fade out
// after a moment, so they're drawn faintly rather than as solid chrome.

export function YouTubeInstreamChrome({
  identity, u, density, showSponsored = true
}: OverlayProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';
  const domain = displayDomain(identity.linkDomain);

  return (
    <div style={overlayBase} aria-hidden>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '34%',
        background: 'linear-gradient(transparent, rgba(0,0,0,.58))'
      }} />

      {/* Advertiser card, bottom-left. In a real in-stream ad this is
          the only durable branding — the creative itself sits behind it,
          so anything the creative puts in this corner is lost. */}
      <div style={{
        position: 'absolute', left: u * 2.4, bottom: u * (compact ? 4 : 6),
        display: 'flex', alignItems: 'center', gap: u * 1.8,
        background: 'rgba(0,0,0,.72)', borderRadius: u * 0.8,
        padding: `${u * 1.4}px ${u * 1.8}px`, maxWidth: '54%'
      }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * (compact ? 5 : 6)} />
        <div style={{ minWidth: 0, lineHeight: 1.25 }}>
          <b style={{ display: 'block', fontSize: u * (compact ? 2.4 : 2.6), fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {identity.name}
          </b>
          {!compact && (
            <span style={{ display: 'block', fontSize: u * 2.2, color: 'rgba(255,255,255,.72)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {domain}
            </span>
          )}
        </div>
        {!compact && (
          <span style={{
            flex: 'none', marginLeft: u * 1.2, background: '#3ea6ff', color: '#0f0f0f',
            borderRadius: u * 0.6, padding: `${u * 1}px ${u * 1.8}px`,
            fontSize: u * 2.4, fontWeight: 600, whiteSpace: 'nowrap'
          }}>
            {cta}
          </span>
        )}
      </div>

      {/* Skip button, bottom-right — sits above the progress bar and is
          the single most reliable thing to keep clear of. */}
      <div style={{
        position: 'absolute', right: 0, bottom: u * (compact ? 4 : 6),
        display: 'flex', alignItems: 'center', gap: u * 1.4,
        background: 'rgba(0,0,0,.62)', color: '#fff',
        border: '1px solid rgba(255,255,255,.35)', borderRight: 'none',
        padding: `${u * 1.6}px ${u * 2.2}px`, fontSize: u * (compact ? 2.4 : 2.6), fontWeight: 500
      }}>
        {!compact && <span>Skip</span>}
        <Icon name="skip" size={u * 3} />
      </div>

      {/* Ad badge, bottom-left of the progress bar. */}
      {showSponsored && !compact && (
        <span style={{
          position: 'absolute', left: u * 2.4, bottom: u * 2.4,
          background: '#ffd400', color: '#0f0f0f', borderRadius: u * 0.4,
          padding: `${u * 0.5}px ${u * 1.1}px`, fontSize: u * 2, fontWeight: 700, letterSpacing: '.04em'
        }}>
          Ad
        </span>
      )}

      {/* Yellow progress bar — the unmistakable in-stream tell. */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: u * 0.7,
        background: 'rgba(255,255,255,.28)'
      }}>
        <i style={{ display: 'block', width: '42%', height: '100%', background: '#ffd400' }} />
      </div>
    </div>
  );
}

// ── Markers Google composites ONTO the creative ──────────────────────
//
// Distinct from the surrounding card furniture: on Display and native
// placements Google stamps these over the image itself, so whatever the
// creative puts in that corner is covered. Drawn inside the media box
// for exactly that reason — an operator needs to see the collision, not
// a tidy card that pretends it doesn't happen.
//
//   AdChoices — the blue ⓘ▸ roundel. Required on Display/native
//               inventory and always in a top corner (right by default).
//   Ad badge  — the literal "Ad" chip Discover and native units draw.
//
// Not applied to the Meta card chromes: Meta's "Sponsored" line lives in
// the card header, never on the creative.

export function GoogleAdMarkers({
  u,
  density,
  showAdBadge = false,
  showSponsored = true,
  corner = 'right'
}: {
  u:               number;
  density:         ChromeDensity;
  showAdBadge?:    boolean;
  showSponsored?:  boolean;
  corner?:         'left' | 'right';
}) {
  const compact = density === 'compact';
  const side = corner === 'right' ? { right: u * 1.4 } : { left: u * 1.4 };

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }} aria-hidden>
      <div style={{
        position: 'absolute', top: u * 1.4, ...side,
        display: 'flex', alignItems: 'center', gap: u * 1.2
      }}>
        {showSponsored && showAdBadge && !compact && (
          <span style={{
            background: 'rgba(0,0,0,.55)', color: '#fff', borderRadius: u * 0.5,
            padding: `${u * 0.5}px ${u * 1.2}px`, fontSize: u * 2.1, fontWeight: 700,
            fontFamily: FONT, letterSpacing: '.04em'
          }}>
            Ad
          </span>
        )}
        <span style={{
          display: 'inline-flex', background: 'rgba(255,255,255,.9)', borderRadius: u * 0.4,
          padding: u * 0.5, color: '#00aecd'
        }}>
          <Icon name="adchoices" size={u * (compact ? 2.6 : 2.4)} style={{ strokeWidth: 2.4 }} />
        </span>
      </div>
    </div>
  );
}
